// Records: how each list is sorted and filtered (Eva, 3 Nov 2026: Carolyn wants her clients A to Z and a dropdown to
// filter). Any field, A to Z or Z to A, and one filter by a field's value. Kept per person and list on this device, so a
// list stays the way she left it.

import { show, type CrmField, type CrmPerson, type CrmRecord, type CrmType } from "./crm";

export type ListView = { sort?: string; desc?: boolean; filterField?: string; filterValue?: string };
/** "updated" sorts by when a record last changed. */
export const UPDATED = "__updated";

const key = (user: string, type: string) => `vera-list-${user}-${type}`;
export function readView(user: string, type: string): ListView {
  try { const v = JSON.parse(localStorage.getItem(key(user, type)) ?? "null"); return v && typeof v === "object" ? v : {}; } catch { return {}; }
}
export function saveView(user: string, type: string, v: ListView) {
  try { localStorage.setItem(key(user, type), JSON.stringify(v)); } catch { /* this visit only */ }
}

/** What a record sorts by for a field: numbers and dates as themselves, everything else as the words people see. */
export function sortKey(f: CrmField, r: CrmRecord, titles: Record<string, string>, people: CrmPerson[]): string | number | null {
  const v = r.values[f.id];
  if (v === undefined || v === null || v === "") return null;
  if ((f.kind === "number" || f.kind === "money") && typeof v === "number") return v;
  if (f.kind === "date" && typeof v === "string") return v;
  return show(f, v, titles, people).toLocaleLowerCase();
}

/** The values a field's filter dropdown offers: what the records actually have (and a dropdown's own choices), A to Z. */
export function filterValues(f: CrmField, rows: CrmRecord[], titles: Record<string, string>, people: CrmPerson[]): string[] {
  const seen = new Set<string>(f.kind === "choice" ? f.options ?? [] : []);
  for (const r of rows) {
    const v = r.values[f.id];
    if (Array.isArray(v)) v.forEach((x) => seen.add(titles[x] ?? x));
    else { const s = show(f, v ?? null, titles, people); if (s) seen.add(s); }
  }
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base", numeric: true }));
}

/** The fields worth filtering by: dropdowns, links, yes/no, people, and short text that repeats (a marina, a city). */
export const filterable = (t: CrmType) => t.fields.filter((f) => !f.hidden && !["longtext", "photos", "links", "money", "number"].includes(f.kind) && f.id !== "ref");

export function applyView(t: CrmType, rows: CrmRecord[], v: ListView, titles: Record<string, string>, people: CrmPerson[]): CrmRecord[] {
  let out = rows;
  const ff = v.filterField ? t.fields.find((f) => f.id === v.filterField) : undefined;
  if (ff && v.filterValue !== undefined && v.filterValue !== "") {
    out = out.filter((r) => {
      const val = r.values[ff.id];
      if (Array.isArray(val)) return val.some((x) => (titles[x] ?? x) === v.filterValue);
      return show(ff, val ?? null, titles, people) === v.filterValue;
    });
  }
  if (!v.sort) return out;
  const dir = v.desc ? -1 : 1;
  if (v.sort === UPDATED) return [...out].sort((a, b) => (a.updated - b.updated) * dir);
  const sf = t.fields.find((f) => f.id === v.sort);
  if (!sf) return out;
  return [...out].sort((a, b) => {
    const x = sortKey(sf, a, titles, people), y = sortKey(sf, b, titles, people);
    if (x === null && y === null) return 0;
    if (x === null) return 1;   // empty always last, whichever way
    if (y === null) return -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
    return String(x).localeCompare(String(y), undefined, { sensitivity: "base", numeric: true }) * dir;
  });
}
